/**
 * helpbotService.js
 *
 * HELPBOT-1 — in-app help bot API calls.
 * Streaming (/ask) uses fetch() directly, same pattern as littoralService.js —
 * axios doesn't support ReadableStream.
 */

import { getAccessToken } from '../lib/auth.js';
import { API_BASE_URL }   from '../lib/constants.js';
import api                from './api.js';

async function getAuthToken() {
  for (const key of ['impersonation', 'portalPreview']) {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.token) return parsed.token;
      }
    } catch { /* malformed entry — continue */ }
  }
  return getAccessToken();
}

/**
 * Stream a help-bot answer as SSE.
 * @param {string}   question
 * @param {string|null} moduleSlug
 * @param {function} onMeta    — called once with { feedbackId }
 * @param {function} onChunk   — called with each text chunk
 * @param {AbortSignal} [signal]
 */
export async function streamHelpbotAsk(question, moduleSlug, onMeta, onChunk, signal) {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/api/helpbot/ask`, {
    method: 'POST',
    headers: {
      Authorization:  `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body:   JSON.stringify({ question, module: moduleSlug }),
    signal,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error ?? 'Ask request failed');
  }

  const reader  = response.body.getReader();
  const decoder = new TextDecoder();
  let   buffer  = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6).trim();
        if (data === '[DONE]') return;
        let parsed;
        try { parsed = JSON.parse(data); } catch { continue; } // skip malformed SSE line
        // RFQ-BULK-UPLOAD-504-1 -- a real {error} payload must propagate to the caller, not be
        // swallowed by the same catch that skips malformed JSON (the throw used to land right
        // back in that catch and vanish silently -- the backend's error message never reached
        // the UI at all, regardless of cause).
        if (parsed.error) throw new Error(parsed.error);
        if (parsed.type === 'meta') onMeta(parsed.feedbackId);
        else if (parsed.text) onChunk(parsed.text);
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/** Record a thumbs up/down signal for a help-bot answer. */
export async function sendHelpbotFeedback(feedbackId, helpful) {
  await api.patch(`/api/helpbot/feedback/${feedbackId}`, { helpful });
}
